import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext({ user: null, loading: true, login: async () => {}, logout: () => {} });

export function AuthProvider({ children }) {
  // user: undefined = checking, null = unauthenticated, object = authed
  const [user, setUser] = useState(undefined);

  // The JWT now lives only in an httpOnly cookie (JS cannot read it), so the
  // session is validated purely via /auth/me. Any legacy localStorage token is
  // proactively cleared. We only probe on auth routes (/login, /admin/*) so the
  // public marketing site never makes an (expected-401) /auth/me request.
  const check = useCallback(async () => {
    try { localStorage.removeItem("adminToken"); } catch { /* storage unavailable — ignore */ }
    const path = window.location.pathname;
    const needsAuth = path === "/login" || path.startsWith("/admin");
    if (!needsAuth) {
      setUser(null);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  // A 401 on a protected admin request (expired/invalid cookie) clears the user;
  // the admin guard then redirects to /login.
  useEffect(() => {
    const onUnauthorized = () => setUser(null);
    window.addEventListener("auth:unauthorized", onUnauthorized);
    return () => window.removeEventListener("auth:unauthorized", onUnauthorized);
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser({ email: data.email });
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* clear locally regardless */ }
    try { localStorage.removeItem("adminToken"); } catch { /* storage unavailable — ignore */ }
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading: user === undefined, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
